import { Card, Grid, SelectChangeEvent, Typography } from '@mui/material';
import IbssButton from '../../../../Components/Buttons/Button/IbssButton';
import { appContext } from '../../../../AppContext';
import { IUserPreferences, IUserPreferencesNode } from '../../../../Providers.Api/UserPreferences/UserPreferenceRepository';
import { InputOption } from '../../../../Components/Inputs/SelectList2/InputSelectBox';
import { PagedResponse } from '../../../../Providers.Api/Models';
import { SystemFilter } from './DataModels';
import IbssInputDropDown from '../../../../Components/Inputs/SelectList/IbssInputDropDown';
import IbssFormControl from '../../../../Components/Forms/FormControl/IbssFormControl';
import Helper from '../../../../Common/Helper';
import { PageHelper } from './PageHelper';
import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { rights } from '../../../../Common/RightsManager';


class SearchResult extends IbssPage<IProps, IState> {

    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private get alert() { return appContext().alert; }
    private get userPreferences() { return appContext().userPreferencesService; }
    private userHasOneLensRights: boolean;
    
    constructor(props: IProps)
    {
        super(props);
        this.state = {
            searchResults: "List",
            searchResultUserPref: PageHelper.getUserPrefrenceJson(),
            isLoading: false,
            buildingOptions: new Array<InputOption>(),
            bookingfilterOptions: new Array<InputOption>(),
            visitorFilterOptions: new Array<InputOption>(),
            cateringFilterOptions: new Array<InputOption>(),
            searchText: null,
            selectedFilter: '',
            selectedVisitorFilter: '',
            selectedCateringFilter: '',
            nodes: new Array<IUserPreferencesNode>(),
            selectedBuildingId: null,
            allFilters: [],
        };
        this.userHasOneLensRights = rights.ONELENS360.has();
    };

    public async componentDidMount(): Promise<void>
    {
        //configure search results input
        const response = this.local.getUserPreferences();
        const buildings = Helper.getAllBuildingsData().sort((a, b) => a.Name < b.Name ? -1 : 1);
        this.pageTitle = this.labels.HubMenuWorkplacePreferences;

        await this.setState({
            buildingOptions: buildings.map(i => new InputOption(i.Name, i.Node_Id ? i.Node_Id.toString() : '')).filter((i) => i.label !== ""),
            searchResults: response?.SearchPrefs?.DefaultSearchResults as string,
            searchResultUserPref: response,
        });
    };

    public searchResultChanged(value: string): void
    {
        this.setState({ searchResults: value });
    };

    public async submit(): Promise<void>
    {
        const payload = {
            ...this.state.searchResultUserPref,
            Nodes: this.state.nodes,
            SearchPrefs:
            {
                DefaultBuilding: this.state.searchResultUserPref.SearchPrefs.DefaultBuilding,
                DefaultSearchResults: this.state.searchResults
            },

        };
        try
        {
            await this.userPreferences.update(payload);
            this.alert.show(this.labels.HubLabelSuccess, this.labels.HubLabelDataHasBeenSavedSuccessfully);
        }
        catch { }
    }

    private async buildingChanged(buildingId: number): Promise<void> 
    {
        const filtersResponse = await appContext().ibssApiClientV2.v2.byNodeid.filters.get<PagedResponse<SystemFilter[]>>({
            nodeId: buildingId,
            select: SystemFilter,
            filter: `Status eq 'StatusActive'`,
        });
    
        const filters = filtersResponse.value;
        const getFiltersByEventType = (eventTypeId: number) => 
            filters.filter((item) => item.EventType_Id === eventTypeId);
    
        const bookingFilters = getFiltersByEventType(8);
        const visitorFilters = getFiltersByEventType(37);
        const cateringFilters = getFiltersByEventType(58);
    
        const bookingfilterOptions = bookingFilters.map(i => new InputOption(i.Name, i.Filter_Id?.toString() || ''));
        const visitorFilterOptions = visitorFilters.map(i => new InputOption(i.Name, i.Filter_Id?.toString() || ''));
        const cateringFilterOptions = cateringFilters.map(i => new InputOption(i.Name, i.Filter_Id?.toString() || ''));

        const defaultNode = this.state.searchResultUserPref.Nodes.find((node) => node.NodeId === buildingId);
    
        const getDefaultFilter = (eventTypeId: string) =>
            defaultNode?.DefaultFilter?.find((item) => item.EventType_Id === eventTypeId)?.Filter_Id || '';
    
        this.setState({
            allFilters: filters,
            bookingfilterOptions,
            visitorFilterOptions,
            cateringFilterOptions,
            selectedBuildingId: buildingId || null,
            selectedFilter: getDefaultFilter('8'),
            selectedVisitorFilter: getDefaultFilter('37'),
            selectedCateringFilter: getDefaultFilter('58'),
            nodes:this.state.searchResultUserPref.Nodes || []
        });
    }

    private async filterChanged(filter: string, type: 'booking' |'catering' | 'visitor'): Promise<void> {
        const userPref = this.local.getUserPreferences();
        const selectedBuildingId = this.state.selectedBuildingId;
    
        const DefaultFloor = userPref.Nodes.find((i: IUserPreferencesNode) => i.NodeId == selectedBuildingId)?.DefaultFloor as number | null;
        const DefaultZone = userPref.Nodes.find((i: IUserPreferencesNode) => i.NodeId == selectedBuildingId)?.DefaultZone as number | null;
    
        const filterDetails = this.state.allFilters.find((item) => item.Filter_Id === filter);
        const newFilter = { Filter_Id: filter ?? '', EventType_Id: filterDetails?.EventType_Id.toString() ?? '' };

        const existingNode = this.state.nodes.find((node) => node.NodeId === selectedBuildingId);
        let updatedDefaultFilter;
        if (existingNode) 
        {
            // Ensure filters array exists, then append the new filter if it's not already included
            updatedDefaultFilter = [...(existingNode.DefaultFilter ?? [])];

    
            const isFilterAlreadyAdded = updatedDefaultFilter.some((f) => f.Filter_Id === newFilter.Filter_Id);
            if (!isFilterAlreadyAdded) 
            {
                updatedDefaultFilter= updatedDefaultFilter.filter((updatedFilter)=>updatedFilter.EventType_Id!==newFilter.EventType_Id);
                updatedDefaultFilter.push(newFilter);
            }
        }
        else 
        {
            updatedDefaultFilter = [newFilter];
        }
    
        const updatedNode: IUserPreferencesNode = 
        {
            NodeId: selectedBuildingId,
            DefaultFloor,
            DefaultZone,
            FavouriteSpaces: existingNode?.FavouriteSpaces ?? [],
            FavouriteAmenities: existingNode?.FavouriteAmenities ?? [],
            FavouriteEquipment: existingNode?.FavouriteEquipment ?? [],
            DefaultFilter: updatedDefaultFilter,
        };
    
        const updatedNodes = [
            ...this.state.nodes.filter((node) => node.NodeId !== selectedBuildingId),
            updatedNode
        ];
    
        if (type === 'booking') 
        {
            this.setState({ selectedFilter: filter, nodes: updatedNodes });
        } 
        else if (type === 'visitor')
        {
            this.setState({ selectedVisitorFilter: filter, nodes: updatedNodes });
        }
        else 
        {
            this.setState({ selectedCateringFilter: filter, nodes: updatedNodes });
        }
    }
    
    public render(): JSX.Element
    {
        return (
            <>
                <Grid container >
                    <Card className='card-userPref'>
                        <Grid
                            container
                            gap={1}
                        >
                            <Typography className='card-userPref-title' variant="h6" gutterBottom>{this.labels.HubTitleSearchResult}</Typography>

                            <Typography className='card-userPref-text mb-32' variant="body2" gutterBottom>{this.labels.HubSubTitleSearchResult}</Typography>

                        </Grid>
                        <Grid container columns={16} className="mt-4 search-reasult">

                            <Grid item xs={8} className="d-flex justify-content-center align-items-center" onClick={() => this.searchResultChanged("List")} sx={{ backgroundColor: this.state.searchResults === "List" ? "var(--ui-primary-pastel)" : null, borderRadius: this.state.searchResults === "List" ? "8px" : null, border: this.state.searchResults === "List" ? "2px solid var(--ui-primary)" : null }}>
                                <div className='p-24'>
                                    <div className='d-block'>
                                        <img alt="List View" src="/images/List view.svg" width="128px" />
                                    </div>
                                    <div className='d-flex justify-content-center mt-8'>
                                        <IbssButton focusRipple className='btn-radius' variant={`${this.state.searchResults === "List" ? "contained" : "text"}`}>{this.labels.HubButtonList}</IbssButton>
                                    </div>
                                </div>
                            </Grid>

                            <Grid item xs={8} className={`d-flex justify-content-center align-items-center ${this.state.searchResults === "Map" ? "search-result-active" : null}`} onClick={() => this.searchResultChanged("Map")}>
                                <div className='p-24'>
                                    <div className='d-block'>
                                        <img alt="Map View" src="/images/Map view.svg" width="128px" />
                                    </div>
                                    <div className='d-flex justify-content-center mt-8'>
                                        <IbssButton focusRipple className='btn-radius' variant={`${this.state.searchResults === "Map" ? "contained" : "text"}`} >{this.labels.HubButtonMap}</IbssButton>
                                    </div>
                                </div>
                            </Grid>
                            {this.userHasOneLensRights && 
                            <>
                            <Grid
                                container
                                gap={1}
                                className="mt-2"
                            >
                                <Typography className='card-userPref-title' variant="h6" gutterBottom>{this.labels.HubTitleDefaultFilter}</Typography>
                                <Typography className='card-userPref-text mb-32' variant="body2" gutterBottom>{this.labels.HubSubTitleDefaultFilter}</Typography>
                            </Grid>
                            <Grid container gap={1}
                                className="mt-2">
                                <IbssFormControl fullWidth className='mb-3'>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubLabelBuilding}
                                        options={this.state.buildingOptions}
                                        value={this.state.selectedBuildingId}
                                        id={"buildingSelection"}
                                        onChange={(e: SelectChangeEvent<string>) => this.buildingChanged(parseInt(e.target.value))}
                                        fullWidth
                                    />
                                </IbssFormControl>
                                <IbssFormControl fullWidth className='mb-3'>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubMenuBookings}
                                        options={this.state.bookingfilterOptions}
                                        value={this.state.selectedFilter}
                                        id={"buildingSelection"}
                                        disabled={!this.state.selectedBuildingId}
                                        onChange={(e: SelectChangeEvent<string>) => this.filterChanged(e.target.value,'booking')}
                                        fullWidth
                                    />
                                </IbssFormControl>
                                <IbssFormControl fullWidth className='mb-3'>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubMenuMyVisitors}
                                        options={this.state.visitorFilterOptions}
                                        value={this.state.selectedVisitorFilter}
                                        id={"visitorSelection"}
                                        disabled={!this.state.selectedBuildingId}
                                        onChange={(e: SelectChangeEvent<string>) => this.filterChanged(e.target.value,'visitor')}
                                        fullWidth
                                    />
                                </IbssFormControl>
                                <IbssFormControl fullWidth className='mb-3'>
                                    <IbssInputDropDown
                                        inputLabel={this.labels.HubLabelCateringTable}
                                        options={this.state.cateringFilterOptions}
                                        value={this.state.selectedCateringFilter}
                                        id={"cateringSelection"}
                                        disabled={!this.state.selectedBuildingId}
                                        onChange={(e: SelectChangeEvent<string>) => this.filterChanged(e.target.value,'catering')}
                                        fullWidth
                                    />
                                </IbssFormControl>
                            </Grid>
                            </>}
                        </Grid>
                    </Card>
                </Grid>
                <div className='btn-right'>
                    <IbssButton variant='contained' onClick={() => this.submit()}>
                        {this.labels.HubButtonSave}
                    </IbssButton>
                </div>
            </>
        )
    }
};

export default SearchResult;

export interface IState
{
    searchResults: string;
    isLoading: boolean;
    searchResultUserPref: IUserPreferences;
    selectedBuildingId: number | null;
    searchText: string | null;
    selectedFilter: string;
    selectedVisitorFilter: string;
    selectedCateringFilter: string;
    buildingOptions: Array<InputOption>;
    bookingfilterOptions: Array<InputOption>;
    visitorFilterOptions: Array<InputOption>;
    cateringFilterOptions: Array<InputOption>;
    nodes: Array<IUserPreferencesNode>;
    allFilters: Array<SystemFilter>
}

export interface IProps
{
}